const FITS = ['closer', 'regular', 'relaxed'];
const MEASUREMENTS = { heightCm: 'height', weightKg: 'weight', chestCm: 'chest', waistCm: 'waist' };
const MEASUREMENT_PRIORITY = ['chestCm', 'waistCm', 'weightKg', 'heightCm'];
const MEASUREMENT_INPUTS = { height: ['cm', 'ft', 'in'], weight: ['kg', 'st', 'lb'], chest: ['chestCm', 'chestIn'], waist: ['waistCm', 'waistIn'] };
const ID = /^[1-9]\d*$/;
const inchCm = 2.54;
const poundKg = 0.45359237;
const rounded = number => Math.round((number + Number.EPSILON) * 10) / 10;
const display = number => String(rounded(number));

export function initialAnswers() {
  return { heightUnit: 'cm', weightUnit: 'kg', cm: '', ft: '', in: '', kg: '', st: '', lb: '', fit: 'regular' };
}

function answersFor(config) {
  return { ...initialAnswers(), ...((config?.profile.chestEnabled || config?.profile.version === 5) && { chestUnit: 'cm', chestCm: '', chestIn: '' }), ...(config?.profile.version === 5 && { waistUnit: 'cm', waistCm: '', waistIn: '' }) };
}

function chestEntered(answers) {
  const keys = answers.chestUnit === 'in' ? ['chestIn'] : !answers.chestUnit || answers.chestUnit === 'cm' ? ['chestCm'] : ['chestCm', 'chestIn'];
  return keys.some(key => answers[key] !== undefined && answers[key] !== '');
}

function measurementEntered(answers, dimension) {
  if (dimension === 'chest' || dimension === 'waist') {
    return [answers[`${dimension}${answers[`${dimension}Unit`] === 'in' ? 'In' : 'Cm'}`]].some(value => value !== undefined && value !== '');
  }
  const height = dimension === 'height', unit = answers[`${dimension}Unit`];
  const keys = unit === (height ? 'cm' : 'kg') ? [unit] : height ? ['ft', 'in'] : ['st', 'lb'];
  return keys.some(key => answers[key] !== undefined && answers[key] !== '');
}

function usedMeasurements(profile) {
  return Object.keys(MEASUREMENTS).filter(key => profile.rules.some(rule => Object.hasOwn(rule, key)));
}

function fourMeasurements(profile, answers) {
  const values = {};
  const used = new Set(usedMeasurements(profile));
  for (const [key, dimension] of Object.entries(MEASUREMENTS)) {
    if (!used.has(key)) { values[key] = null; continue; }
    const entered = measurementEntered(answers, dimension);
    const value = measurement(answers, dimension);
    if (entered && value === null) return null;
    values[key] = entered ? value : null;
  }
  return Object.values(values).some(value => value !== null) ? values : null;
}

function numeric(value, whole = false) {
  if (typeof value !== 'string' || !(whole ? /^\d+$/ : /^\d+(?:\.\d)?$/).test(value)) return null;
  const number = Number(value);
  return Number.isSafeInteger(Math.round(number * 10)) ? number : null;
}

export function measurement(answers, dimension) {
  if (dimension === 'chest' || dimension === 'waist') {
    const unit = answers[`${dimension}Unit`] || 'cm';
    if (!['cm', 'in'].includes(unit)) return null;
    const value = numeric(answers[`${dimension}${unit === 'cm' ? 'Cm' : 'In'}`]);
    if (value === null || value <= 0) return null;
    if (unit === 'cm') return value;
    const numerator = Math.round(value * 10) * 254;
    return Number.isSafeInteger(numerator) ? numerator / 1000 : null;
  }
  const height = dimension === 'height';
  const unit = answers[height ? 'heightUnit' : 'weightUnit'];
  const metricUnit = height ? 'cm' : 'kg';
  if (unit === metricUnit) {
    const number = numeric(answers[metricUnit]);
    return number !== null && number > 0 ? number : null;
  }
  if (unit !== (height ? 'ft-in' : 'st-lb')) return null;
  const major = numeric(answers[height ? 'ft' : 'st'], true);
  const minor = numeric(answers[height ? 'in' : 'lb']);
  const divisor = height ? 12 : 14;
  if (major === null || minor === null || minor >= divisor) return null;
  const tenths = major * divisor * 10 + Math.round(minor * 10);
  const numerator = tenths * (height ? 254 : 45359237);
  if (tenths <= 0 || !Number.isSafeInteger(numerator)) return null;
  return numerator / (height ? 1000 : 1000000000);
}

export function switchUnits(answers, dimension, next) {
  if (dimension === 'chest' || dimension === 'waist') {
    if (!['cm', 'in'].includes(next) || (answers[`${dimension}Unit`] || 'cm') === next) return { ...answers };
    const value = measurement(answers, dimension);
    return { ...answers, [`${dimension}Unit`]: next, [`${dimension}${next === 'cm' ? 'Cm' : 'In'}`]: value === null ? (measurementEntered(answers, dimension) ? 'invalid' : '') : display(value / (next === 'in' ? inchCm : 1)) };
  }
  const height = dimension === 'height';
  const key = height ? 'heightUnit' : 'weightUnit';
  if (![height ? 'cm' : 'kg', height ? 'ft-in' : 'st-lb'].includes(next)) return { ...answers };
  if (answers[key] === next) return { ...answers };
  const value = measurement(answers, dimension);
  const result = { ...answers, [key]: next };
  const metric = height ? 'cm' : 'kg';
  if (next === metric) result[metric] = value === null ? '' : display(value);
  else {
    const totalTenths = value === null ? null : Math.round(value / (height ? inchCm : poundKg) * 10);
    const divisor = height ? 120 : 140;
    result[height ? 'ft' : 'st'] = totalTenths === null ? '' : String(Math.floor(totalTenths / divisor));
    result[height ? 'in' : 'lb'] = totalTenths === null ? '' : String((totalTenths % divisor) / 10);
  }
  return result;
}

export function validateProfile(config) {
  try {
    if (!config || !ID.test(config.productId) || typeof config.productId !== 'string') return null;
    if (typeof config.formId !== 'string' || !config.formId || typeof config.sectionId !== 'string' || !config.sectionId || typeof config.handle !== 'string' || !config.handle) return null;
    const profile = config.profile;
    if (!profile || ![1, 2, 3, 5].includes(profile.version) || profile.productId !== config.productId) return null;
    const four = profile.version === 5;
    if (four && (profile.mode !== 'four-measurement' || !Object.hasOwn(MEASUREMENTS, profile.primaryMeasurement))) return null;
    const table = profile.version === 2;
    const weightBands = profile.version === 3;
    const chest = weightBands && profile.chestEnabled === true;
    if (weightBands && Object.hasOwn(profile, 'chestEnabled') && typeof profile.chestEnabled !== 'boolean') return null;
    if (table && (profile.status !== 'provisional' || profile.mode !== 'inclusive-table')) return null;
    if (weightBands && (profile.status !== 'provisional' || profile.mode !== 'weight-bands')) return null;
    if (!Array.isArray(config.options) || config.options.length < 1 || config.options.length > 3) return null;
    const valueIds = new Set();
    for (const [index, option] of config.options.entries()) {
      if (option.position !== index + 1 || !Array.isArray(option.values) || !option.values.length) return null;
      for (const value of option.values) {
        if (typeof value.id !== 'string' || !ID.test(value.id) || valueIds.has(value.id) || typeof value.name !== 'string' || !value.name) return null;
        valueIds.add(value.id);
      }
    }
    if (!Number.isInteger(profile.optionPosition)) return null;
    const option = config.options.find(item => item.position === profile.optionPosition);
    if (!option || !Array.isArray(profile.rules) || !profile.rules.length || profile.rules.length > 500) return null;
    const ids = new Set(option.values.map(value => value.id));
    if ((table || weightBands || four) && (profile.rules.length > (four ? 50 : 20) || new Set(option.values.map(value => value.name)).size !== option.values.length)) return null;
    const regularIds = new Set();
    const groupedLabels = new Set();
    for (const [index, rule] of profile.rules.entries()) {
      if (four) {
        if (!rule || typeof rule !== 'object' || typeof rule.sizeLabel !== 'string' || !rule.sizeLabel.trim() || rule.sizeLabel !== rule.sizeLabel.trim() || !Object.hasOwn(rule, profile.primaryMeasurement)) return null;
        for (const dimension of Object.keys(MEASUREMENTS)) if (Object.hasOwn(rule, dimension)) {
          const range = rule[dimension];
          if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max) || range.min <= 0 || range.max <= range.min || typeof range.maxInclusive !== 'boolean') return null;
        }
        if (Object.hasOwn(rule, 'choices')) {
          if (Object.hasOwn(rule, 'outcomes') || !Array.isArray(rule.choices) || rule.choices.length > option.values.length) return null;
          const choiceIds = new Set();
          for (const choice of rule.choices) {
            const native = option.values.find(value => value.id === choice?.optionValueId);
            if (!native || native.name !== choice.label || choiceIds.has(native.id)) return null;
            choiceIds.add(native.id);
          }
        } else if (!rule.outcomes || Object.keys(rule.outcomes).length !== 1 || !ids.has(rule.outcomes.regular)) return null;
        continue;
      }
      for (const dimension of weightBands ? ['weightKg'] : ['heightCm', 'weightKg']) {
        const range = rule[dimension];
        if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max) || range.min <= 0 || range.max <= range.min) return null;
      }
      if (chest) {
        const range = rule.chestCm;
        if (!range || !Number.isInteger(range.min) || !Number.isInteger(range.max) || range.min <= 0 || range.max <= range.min || Object.hasOwn(rule, 'choices')) return null;
        if (index && profile.rules[index - 1].chestCm.max >= range.min) return null;
      }
      if (weightBands && Object.hasOwn(rule, 'choices')) {
        if (Object.hasOwn(rule, 'outcomes') || typeof rule.sizeLabel !== 'string' || !/^[1-9]\d*(?:\.\d+)?$/.test(rule.sizeLabel) || groupedLabels.has(rule.sizeLabel)) return null;
        if (!Array.isArray(rule.choices) || rule.choices.length < 2 || rule.choices.length > option.values.length) return null;
        groupedLabels.add(rule.sizeLabel);
        for (const choice of rule.choices) {
          const native = option.values.find(value => value.id === choice?.optionValueId);
          const prefix = `${rule.sizeLabel} - `;
          if (!native || regularIds.has(native.id) || choice.label !== native.name || !choice.label.startsWith(prefix) || !choice.label.slice(prefix.length).trim()) return null;
          regularIds.add(native.id);
        }
      } else {
        if (!rule.outcomes) return null;
        if (table || weightBands) {
          if (!ids.has(rule.outcomes.regular) || regularIds.has(rule.outcomes.regular)) return null;
          regularIds.add(rule.outcomes.regular);
          if (Object.keys(rule.outcomes).some(fit => !(weightBands ? ['regular'] : FITS).includes(fit) || !ids.has(rule.outcomes[fit]))) return null;
        } else if (FITS.some(fit => !ids.has(rule.outcomes[fit]))) return null;
      }
      if (weightBands) {
        if (typeof rule.weightKg.maxInclusive !== 'boolean' || (rule.weightKg.maxInclusive && index !== profile.rules.length - 1)) return null;
        if (index && profile.rules[index - 1].weightKg.max > rule.weightKg.min) return null;
      }
    }
    for (let i = 0; !table && !weightBands && !four && i < profile.rules.length; i++) for (let j = i + 1; j < profile.rules.length; j++) {
      const a = profile.rules[i]; const b = profile.rules[j];
      if (['heightCm', 'weightKg'].every(key => a[key].min < b[key].max && b[key].min < a[key].max)) return null;
    }
    return profile;
  } catch { return null; }
}

export function supportedFits(config) {
  const profile = validateProfile(config);
  return profile ? (profile.version === 1 ? [...FITS] : [3, 5].includes(profile.version) ? ['regular'] : FITS.filter(fit => profile.rules.every(rule => Object.hasOwn(rule.outcomes, fit)))) : [];
}

export function recommend(config, answers) {
  const profile = validateProfile(config);
  if (!profile) return null;
  if (profile.version === 5) {
    const values = fourMeasurements(profile, answers);
    if (!values) return null;
    const basis = [profile.primaryMeasurement, ...MEASUREMENT_PRIORITY].find(key => values[key] !== null);
    const contains = (rule, key) => values[key] >= rule[key].min && (rule[key].maxInclusive ? values[key] <= rule[key].max : values[key] < rule[key].max);
    let matches = profile.rules.filter(rule => Object.hasOwn(rule, basis) && contains(rule, basis));
    // A unique basis is sufficient. Supporting measurements only resolve an overlap;
    // they cannot reject that unique match or replace an out-of-range basis.
    const used = [basis];
    if (matches.length > 1) {
      const supporting = MEASUREMENT_PRIORITY.filter(key => key !== basis && values[key] !== null && matches.some(rule => Object.hasOwn(rule, key)));
      matches = matches.filter(rule => supporting.every(key => !Object.hasOwn(rule, key) || contains(rule, key)));
      // Missing ranges are unknown: they cannot make a row win by default when a
      // different row disagrees with supplied supporting measurements.
      if (matches.some(rule => supporting.some(key => !Object.hasOwn(rule, key)))) return null;
      used.push(...supporting);
    }
    if (!matches.length) return null;
    const usedKeys = Object.keys(MEASUREMENTS).filter(key => used.includes(key));
    const rule = matches[0], result = { productId: config.productId, optionPosition: profile.optionPosition, label: rule.sizeLabel, measurements: Object.fromEntries(Object.keys(MEASUREMENTS).map(key => [key, usedKeys.includes(key) ? values[key] : null])), usedMeasurements: usedKeys, basis };
    if (matches.length > 1) {
      // Preserve every matching approved size. Native option order is presentation
      // only: no midpoint, rule order or availability chooses a winner.
      const sizeLabels = [...new Set(matches.map(match => match.sizeLabel))];
      const choiceIds = new Set(matches.flatMap(match => match.choices ? match.choices.map(choice => choice.optionValueId) : [match.outcomes.regular]));
      const choices = config.options.find(option => option.position === profile.optionPosition).values
        .filter(value => choiceIds.has(value.id)).map(value => ({ optionValueId: value.id, label: value.name }));
      if (sizeLabels.length === 1 && choices.length === 1) return { ...result, optionValueId: choices[0].optionValueId, label: choices[0].label };
      const canRefine = Object.keys(MEASUREMENTS).some(key => values[key] === null && matches.every(match => Object.hasOwn(match, key))
        && new Set(matches.map(match => `${match[key].min}:${match[key].max}:${match[key].maxInclusive}`)).size > 1);
      return { ...result, label: sizeLabels.join(' / '), multipleSizes: sizeLabels.length > 1, canRefine, choices };
    }
    if (rule.choices?.length === 1) return { ...result, optionValueId: rule.choices[0].optionValueId, label: rule.choices[0].label };
    if (rule.choices) return { ...result, choices: rule.choices.map(choice => ({ ...choice })) };
    const value = config.options.find(option => option.position === profile.optionPosition).values.find(value => value.id === rule.outcomes.regular);
    return { ...result, optionValueId: value.id, label: value.name };
  }
  const weightBands = profile.version === 3;
  const useChest = weightBands && profile.chestEnabled === true && chestEntered(answers);
  const chestCm = useChest ? measurement(answers, 'chest') : null;
  const heightCm = weightBands ? null : measurement(answers, 'height'); const weightKg = measurement(answers, 'weight');
  if ((!weightBands && heightCm === null) || (useChest ? chestCm === null : weightKg === null) || !supportedFits(config).includes(answers.fit)) return null;
  const matches = profile.rules.filter(rule => useChest
    ? Math.round(chestCm) >= rule.chestCm.min && Math.round(chestCm) <= rule.chestCm.max
    : weightBands
    ? weightKg >= rule.weightKg.min && (rule.weightKg.maxInclusive ? weightKg <= rule.weightKg.max : weightKg < rule.weightKg.max)
    : heightCm >= rule.heightCm.min && weightKg >= rule.weightKg.min && (profile.version === 2 ? heightCm <= rule.heightCm.max && weightKg <= rule.weightKg.max : heightCm < rule.heightCm.max && weightKg < rule.weightKg.max));
  if (matches.length !== 1) return null;
  if (weightBands && matches[0].choices) return { productId: config.productId, optionPosition: profile.optionPosition, label: matches[0].sizeLabel, choices: matches[0].choices.map(choice => ({ ...choice })), weightKg, fit: answers.fit };
  const optionValueId = matches[0].outcomes[answers.fit];
  const value = config.options.find(option => option.position === profile.optionPosition).values.find(item => item.id === optionValueId);
  return { productId: config.productId, optionPosition: profile.optionPosition, optionValueId, label: value.name, ...(!weightBands && { heightCm }), ...(useChest ? { chestCm, basis: 'chest' } : { weightKg }), fit: answers.fit };
}

function nativeContext(element, config) {
  const product = element.closest('raith-product');
  const form = element.ownerDocument.getElementById(config.formId);
  if (!element.isConnected || !product?.isConnected || form?.tagName !== 'FORM' || !product.contains(form)) return null;
  if (product.dataset.formId !== config.formId || product.dataset.sectionId !== config.sectionId) return null;
  const pickers = [...product.querySelectorAll('variant-picker')].filter(picker => picker.getAttribute('form-id') === config.formId && picker.getAttribute('section-id') === config.sectionId && picker.getAttribute('handle') === config.handle && picker.getAttribute('context') === 'main_product');
  if (pickers.length !== 1) return null;
  const picker = pickers[0];
  if (!product.classList.contains('vl-enhanced') || typeof product.setPending !== 'function' || typeof picker.selectCombination !== 'function' || !picker.selectedVariant) return null;
  const inputs = [...picker.querySelectorAll('input[type="radio"][data-option-position]')].filter(input => input.form === form && input.getAttribute('form') === config.formId);
  const formOptions = [...form.elements].filter(input => input.matches('input[data-option-position]'));
  if (formOptions.length !== inputs.length || formOptions.some(input => !inputs.includes(input))) return null;
  if (inputs.some(input => !config.options.some(option => option.position === Number(input.dataset.optionPosition) && option.values.some(value => value.id === input.value)))) return null;
  const selected = {};
  for (const option of config.options) {
    const group = inputs.filter(input => Number(input.dataset.optionPosition) === option.position);
    const checked = group.filter(input => input.checked);
    if (checked.length !== 1 || !option.values.some(value => value.id === checked[0].value)) return null;
    if (new Set(group.map(input => input.value)).size !== group.length || group.some(input => input.name !== `${config.formId}-option${option.position}`)) return null;
    selected[option.position] = checked[0].value;
  }
  return { product, form, picker, inputs, selected };
}

function targetInput(context, result) {
  const inputs = context.inputs.filter(input => Number(input.dataset.optionPosition) === result.optionPosition && input.value === result.optionValueId);
  if (inputs.length !== 1) return null;
  const input = inputs[0];
  if (input.disabled || input.matches(':disabled') || input.hasAttribute('data-product-url') || [...input.labels].some(label => label.classList.contains('is-disabled'))) return null;
  return input;
}

export function registerSizeCalculator(scope = globalThis) {
  if (!scope.customElements || !scope.HTMLElement || scope.customElements.get('raith-size-calculator')) return;
  const instances = new Set();
  const markerSelector = 'script[data-raith-calculator-profile]';
  const refreshGlobal = () => { for (const instance of instances) instance.refreshGlobal(); };
  const touchesMarker = node => node.nodeType === 1 && (node.matches(markerSelector) || Boolean(node.querySelector(markerSelector)));
  const globalObserver = new scope.MutationObserver(records => {
    if (records.some(record => record.target.nodeType === 1 && record.target.matches(markerSelector)
      || record.target.parentElement?.matches(markerSelector)
      || [...record.addedNodes, ...record.removedNodes].some(touchesMarker))) refreshGlobal();
  });
  globalObserver.observe(scope.document.documentElement, { childList: true, characterData: true, subtree: true });
  scope.document.addEventListener('shopify:section:load', refreshGlobal);
  scope.document.addEventListener('shopify:section:unload', () => scope.queueMicrotask(refreshGlobal));
  class RaithSizeCalculator extends scope.HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.answers = initialAnswers(); this.hiddenSiblings = new Map();
      instances.add(this);
      this.controller = new scope.AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', event => this.handleClick(event), { signal });
      this.addEventListener('input', event => {
        const input = event.target.closest('[data-measure]'); if (!input || input.disabled || !this.config) return;
        this.answers[input.dataset.measure] = input.validity.badInput ? 'invalid' : input.value; this.invalidate(); this.updateAnswers();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-measure]')) { event.preventDefault(); if (!this.querySelector('[data-size-calculate]').disabled) this.calculate(); }
      }, { signal });
      this.drawer = this.closest('vl-size-drawer');
      this.drawer?.addEventListener('dialog:after-hide', event => { if (event.target === this.drawer) { this.back(false); this.answers = answersFor(this.config); this.updateAnswers(); } }, { signal });
      this.loadConfig();
      if (['raith-product', 'variant-picker', 'vl-size-drawer'].some(name => !scope.customElements.get(name))) {
        Promise.all(['raith-product', 'variant-picker', 'vl-size-drawer'].map(name => scope.customElements.whenDefined(name))).then(() => {
          if (!signal.aborted) this.loadConfig();
        });
      }
      this.observer = new scope.MutationObserver(records => {
        const node = this.querySelector('[data-size-config]');
        const strings = this.querySelector('[data-size-strings]');
        if (node !== this.configNode || strings !== this.stringsNode || records.some(record => [node, strings].some(source => source && (record.target === source || source.contains(record.target))))) this.loadConfig();
      });
      this.observer.observe(this, { childList: true, characterData: true, subtree: true });
    }
    disconnectedCallback() {
      instances.delete(this);
      this.controller?.abort(); this.contextController?.abort(); this.observer?.disconnect();
      this.cancelAttempt(); this.restoreNative();
      this.config = null; this.result = null; this.answers = initialAnswers();
    }
    refreshGlobal() {
      if (!this.isConnected) return;
      const nodes = [...this.ownerDocument.querySelectorAll(markerSelector)];
      const signature = JSON.stringify(nodes.map(node => node.textContent));
      if (signature !== this.globalSignature) this.loadConfig();
    }
    loadConfig() {
      this.stringsNode = this.querySelector('[data-size-strings]');
      try { this.strings = JSON.parse(this.stringsNode?.textContent); } catch { this.strings = {}; }
      this.back(false); this.contextController?.abort(); this.config = null;
      this.configNode = this.querySelector('[data-size-config]');
      const markers = [...this.ownerDocument.querySelectorAll(markerSelector)];
      this.globalSignature = JSON.stringify(markers.map(node => node.textContent));
      this.hidden = false;
      try {
        const config = JSON.parse(this.configNode?.textContent);
        if (markers.length) {
          if (markers.length !== 1 || markers[0].textContent.length > 65536) throw Error('Invalid shared profile marker');
          const packet = JSON.parse(markers[0].textContent);
          if (packet.productId !== config.productId || !packet.profile || typeof packet.profile !== 'object') throw Error('Wrong shared profile');
          if (packet.profile.disabled === true) this.hidden = true;
          config.profile = packet.profile;
          delete config.profileJson;
          if (packet.strings && typeof packet.strings === 'object' && !Array.isArray(packet.strings)) {
            for (const [key, value] of Object.entries(packet.strings)) if (typeof value === 'string') this.strings[key] = value;
          }
        } else if (Object.hasOwn(config, 'profileJson')) {
          if (Object.hasOwn(config, 'profile') || typeof config.profileJson !== 'string' || config.profileJson.length > 65536) throw Error('Invalid profile input');
          config.profile = JSON.parse(config.profileJson); delete config.profileJson;
        }
        if (validateProfile(config)) this.config = config;
      } catch {}
      this.querySelector('[data-size-entry]').hidden = !this.config;
      this.querySelector('[data-size-unavailable]').hidden = Boolean(this.config);
      this.answers = answersFor(this.config); this.updateAnswers();
      this.text('[data-size-heading]', this.message('heading'));
      this.text('.raith-size-calculator__entry strong', this.message('entry_heading'));
      this.text('[data-size-four-guidance]', this.message('four_guidance'));
      if (!this.config) return;
      this.contextController = new scope.AbortController(); const signal = this.contextController.signal;
      const context = nativeContext(this, this.config);
      if (!context) { this.config = null; this.querySelector('[data-size-entry]').hidden = true; this.querySelector('[data-size-unavailable]').hidden = false; return; }
      context.product.addEventListener('change', event => {
        if (event.target.matches('input[data-option-position]') && !this.dispatching) this.invalidate();
      }, { capture: true, signal });
      context.form.addEventListener('product:rerender', event => {
        if (event.detail?.productChange) { this.config = null; this.back(false); this.querySelector('[data-size-entry]').hidden = true; this.querySelector('[data-size-unavailable]').hidden = false; }
        else if (!this.attempt) this.invalidate();
        scope.queueMicrotask(() => { if (!signal.aborted && this.hasAttribute('data-open')) this.hideNative(); });
      }, { signal });
    }
    cancelAttempt() {
      if (this.attempt) { scope.clearTimeout(this.attempt.timeout); this.attempt.controller.abort(); this.attempt = null; }
      this.removeAttribute('aria-busy');
    }
    invalidate() {
      this.cancelAttempt(); this.result = null;
      const result = this.querySelector('[data-size-result]'); if (result) result.hidden = true;
      const answers = this.querySelector('[data-size-answers]'); if (answers) answers.hidden = false;
      this.text('[data-size-heading]', this.message('heading')); this.text('[data-size-status]', '');
    }
    message(key, values = {}) {
      const template = this.strings?.[key];
      return typeof template === 'string' ? template.replace(/__([A-Z]+)__/g, (token, name) => Object.hasOwn(values, name.toLowerCase()) ? String(values[name.toLowerCase()]) : token) : '';
    }
    formattedMeasurement(answers, dimension) {
      if (dimension === 'chest' || dimension === 'waist') return this.message(`format_${dimension}_${answers[`${dimension}Unit`] || 'cm'}`, { [dimension]: answers[`${dimension}${answers[`${dimension}Unit`] === 'in' ? 'In' : 'Cm'}`] });
      return this.message(`format_${answers[`${dimension}Unit`].replace('-', '_')}`, answers);
    }
    text(selector, value) { const node = this.querySelector(selector); if (node) node.textContent = value; }
    showCalculatorTitle() {
      this.restoreDrawerTitle();
      const headings = [...(this.drawer?.children || [])].filter(node => node.getAttribute('slot') === 'header' && node.id === this.drawer.getAttribute('aria-labelledby'));
      if (headings.length !== 1 || !this.dataset.drawerTitle) return;
      const heading = headings[0], replacement = this.ownerDocument.createTextNode(this.dataset.drawerTitle);
      this.drawerTitle = { heading, nodes: [...heading.childNodes], replacement, value: replacement.data };
      heading.replaceChildren(replacement);
    }
    restoreDrawerTitle() {
      const state = this.drawerTitle; this.drawerTitle = null;
      if (state && state.heading.childNodes.length === 1 && state.heading.firstChild === state.replacement && state.replacement.data === state.value) state.heading.replaceChildren(...state.nodes);
    }
    hideNative() {
      for (const child of this.parentElement.children) {
        if (child === this || child.matches('.vl-variant-error')) continue;
        if (!this.hiddenSiblings.has(child)) this.hiddenSiblings.set(child, child.hidden);
        child.hidden = true;
        child.setAttribute('data-size-calculator-hidden', '');
      }
    }
    restoreNative() {
      this.restoreDrawerTitle();
      for (const [node, hidden] of this.hiddenSiblings || []) { node.hidden = hidden; node.removeAttribute('data-size-calculator-hidden'); }
      this.hiddenSiblings?.clear();
    }
    back(focus = true) {
      this.invalidate(); this.removeAttribute('data-open'); this.restoreNative();
      const panel = this.querySelector('[data-size-panel]'); if (panel) panel.hidden = true;
      const entry = this.querySelector('[data-size-entry]'); if (entry) { entry.hidden = !this.config; if (focus && !entry.hidden) entry.focus(); }
    }
    handleClick(event) {
      const button = event.target.closest('button'); if (!button || button.disabled || !this.config) return;
      if (button.hasAttribute('data-size-entry')) {
        if (!nativeContext(this, this.config)) return;
        this.setAttribute('data-open', ''); this.showCalculatorTitle(); this.hideNative(); button.hidden = true;
        this.querySelector('[data-size-panel]').hidden = false; this.updateAnswers(); this.querySelector('[data-size-heading]').focus();
      } else if (button.hasAttribute('data-size-back')) this.back();
      else if (button.dataset.chestUnit || button.dataset.waistUnit) {
        const dimension = button.dataset.chestUnit ? 'chest' : 'waist';
        if (this.config.profile.version === 5 && measurementEntered(this.answers, dimension) && measurement(this.answers, dimension) === null) return;
        this.answers = switchUnits(this.answers, dimension, button.dataset.chestUnit || button.dataset.waistUnit);
        this.invalidate(); this.updateAnswers();
      }
      else if (button.dataset.heightUnit || button.dataset.weightUnit) {
        const dimension = button.dataset.heightUnit ? 'height' : 'weight';
        // Keep invalid optional input visible and editable in its current unit.
        // Converting it to blank inputs would leave an invisible invalid answer.
        if (this.config.profile.version === 5 && measurementEntered(this.answers, dimension) && measurement(this.answers, dimension) === null) return;
        this.answers = switchUnits(this.answers, dimension, button.dataset.heightUnit || button.dataset.weightUnit);
        this.invalidate(); this.updateAnswers();
      } else if (button.dataset.fit) { this.answers.fit = button.dataset.fit; this.invalidate(); this.updateAnswers(); }
      else if (button.hasAttribute('data-size-calculate')) this.calculate();
      else if (button.hasAttribute('data-size-edit')) { this.invalidate(); this.updateAnswers(); this.querySelector('[data-size-heading]').focus(); }
      else if (button.hasAttribute('data-size-select')) this.selectSize();
      else if (button.hasAttribute('data-size-choice')) this.selectSize(button.dataset.sizeChoice);
    }
    updateAnswers() {
      if (!this.querySelector('[data-size-answers]')) return;
      const four = this.config?.profile.version === 5;
      this.toggleAttribute('data-four-measurement', four);
      const weightBands = this.config?.profile.version === 3;
      const visible = new Set(!this.config ? [] : four ? usedMeasurements(this.config.profile).map(key => MEASUREMENTS[key])
        : [...(weightBands ? [] : ['height']), 'weight', ...(weightBands && this.config.profile.chestEnabled === true ? ['chest'] : [])]);
      const chest = visible.has('chest');
      for (const [dimension, keys] of Object.entries(MEASUREMENT_INPUTS)) {
        const active = visible.has(dimension), fieldset = this.querySelector(`[data-size-${dimension}]`);
        if (fieldset) { fieldset.hidden = !active; fieldset.disabled = !active; }
        for (const control of fieldset?.querySelectorAll('input, button') || []) control.disabled = !active;
        if (!active) {
          for (const key of keys) if (Object.hasOwn(this.answers, key)) this.answers[key] = '';
          for (const input of fieldset?.querySelectorAll('[data-measure]') || []) input.removeAttribute('aria-invalid');
          this.text(`[data-${dimension}-hint]`, ''); this.text(`[data-${dimension}-conversion]`, '');
        }
      }
      this.querySelector('[data-size-four-guidance]')?.toggleAttribute('hidden', !four);
      const fitFieldset = this.querySelector('.raith-size-calculator__fit');
      if (fitFieldset) fitFieldset.hidden = weightBands || four;
      const weightGuidance = this.querySelector('[data-size-weight-guidance]');
      if (weightGuidance) weightGuidance.hidden = !weightBands || chest;
      const chestGuidance = this.querySelector('[data-size-chest-guidance]');
      if (chestGuidance) chestGuidance.hidden = !chest || four;
      const required = this.querySelector('[data-size-measurement-required]');
      if (required) required.hidden = !chest || four;
      this.text('[data-size-entry-subheading]', this.message(four ? 'four_entry_subheading' : chest ? 'chest_entry_subheading' : weightBands ? 'weight_entry_subheading' : 'entry_subheading'));
      for (const input of this.querySelectorAll('[data-measure]')) {
        const value = this.answers[input.dataset.measure] ?? '';
        if (input.value !== value && !(value === 'invalid' && input.validity.badInput)) input.value = value;
      }
      if (chest) {
        const unit = this.answers.chestUnit, metric = measurement(this.answers, 'chest'), started = chestEntered(this.answers);
        for (const node of this.querySelectorAll('[data-chest-fields]')) node.hidden = node.dataset.chestFields !== unit;
        for (const button of this.querySelectorAll('[data-chest-unit]')) button.setAttribute('aria-pressed', String(button.dataset.chestUnit === unit));
        const hint = this.querySelector('[data-chest-hint]');
        hint.id = `${this.config.formId}-chest-hint`;
        hint.textContent = this.message(started && metric === null ? 'invalid_chest' : four ? 'four_chest_hint' : 'chest_hint', { hint: this.message(four ? 'four_chest_hint' : 'chest_hint') });
        const input = this.querySelector(`[data-measure="${unit === 'cm' ? 'chestCm' : 'chestIn'}"]`);
        input.setAttribute('aria-invalid', String(started && metric === null)); input.setAttribute('aria-describedby', hint.id);
        const alternate = switchUnits(this.answers, 'chest', unit === 'cm' ? 'in' : 'cm');
        this.text('[data-chest-conversion]', metric === null ? '' : this.message('approximate', { value: this.formattedMeasurement(alternate, 'chest') }));
      }
      if (visible.has('waist')) {
        const unit = this.answers.waistUnit, value = measurement(this.answers, 'waist'), started = measurementEntered(this.answers, 'waist');
        for (const node of this.querySelectorAll('[data-waist-fields]')) node.hidden = node.dataset.waistFields !== unit;
        for (const button of this.querySelectorAll('[data-waist-unit]')) button.setAttribute('aria-pressed', String(button.dataset.waistUnit === unit));
        const hint = this.querySelector('[data-waist-hint]');
        hint.id = `${this.config.formId}-waist-hint`;
        hint.textContent = this.message(started && value === null ? 'invalid_waist' : 'waist_hint');
        const input = this.querySelector(`[data-measure="${unit === 'cm' ? 'waistCm' : 'waistIn'}"]`);
        input.setAttribute('aria-invalid', String(started && value === null)); input.setAttribute('aria-describedby', hint.id);
        const alternate = switchUnits(this.answers, 'waist', unit === 'cm' ? 'in' : 'cm');
        this.text('[data-waist-conversion]', value === null ? '' : this.message('approximate', { value: this.formattedMeasurement(alternate, 'waist') }));
      }
      for (const dimension of Object.values(MEASUREMENTS)) {
        const note = this.querySelector(`[data-size-role="${dimension}"]`);
        if (!note) continue;
        note.hidden = !four || !visible.has(dimension);
        note.textContent = note.hidden ? '' : this.message('four_optional', { measurement: this.message(dimension) });
      }
      for (const dimension of ['height', 'weight']) {
        if (!visible.has(dimension)) continue;
        const height = dimension === 'height'; const unit = this.answers[`${dimension}Unit`];
        for (const node of this.querySelectorAll(`[data-${dimension}-fields]`)) node.hidden = node.getAttribute(`data-${dimension}-fields`) !== unit;
        for (const button of this.querySelectorAll(`[data-${dimension}-unit]`)) button.setAttribute('aria-pressed', String(button.getAttribute(`data-${dimension}-unit`) === unit));
        const metric = measurement(this.answers, dimension);
        const keys = unit === (height ? 'cm' : 'kg') ? [unit] : height ? ['ft', 'in'] : ['st', 'lb'];
        const started = keys.some(key => this.answers[key] !== '');
        const hint = this.message(`hint_${unit.replace('-', '_')}`);
        const hintNode = this.querySelector(`[data-${dimension}-hint]`);
        hintNode.id = `${this.config?.formId || 'calculator'}-${dimension}-hint`;
        hintNode.textContent = started && metric === null ? this.message(`invalid_${dimension}`, { hint }) : hint;
        for (const key of keys) { const input = this.querySelector(`[data-measure="${key}"]`); input.setAttribute('aria-invalid', String(started && metric === null)); input.setAttribute('aria-describedby', hintNode.id); }
        const alternate = switchUnits(this.answers, dimension, unit === (height ? 'cm' : 'kg') ? (height ? 'ft-in' : 'st-lb') : (height ? 'cm' : 'kg'));
        const converted = metric === null ? '' : this.message('approximate', { value: this.formattedMeasurement(alternate, dimension) });
        this.text(`[data-${dimension}-conversion]`, converted);
      }
      const fits = supportedFits(this.config);
      for (const button of this.querySelectorAll('[data-fit]')) {
        button.disabled = !fits.includes(button.dataset.fit);
        button.setAttribute('aria-pressed', String(button.dataset.fit === this.answers.fit));
      }
      const provisional = this.querySelector('[data-size-provisional]');
      if (provisional) provisional.hidden = this.config?.profile.version !== 2;
      const fitHelp = this.querySelector('[data-size-fit-help]');
      if (fitHelp) {
        const describeFits = Boolean(this.config) && !weightBands && !four && fits.length < FITS.length;
        const fieldset = this.querySelector('.raith-size-calculator__fit');
        fitHelp.id = `${this.config?.formId || 'calculator'}-fit-help`;
        fitHelp.hidden = !describeFits;
        fitHelp.textContent = describeFits ? this.message('available_fits', { fits: fits.map(fit => this.message(`fit_${fit}`)).join(', ') }) : '';
        if (describeFits) fieldset?.setAttribute('aria-describedby', fitHelp.id);
        else fieldset?.removeAttribute('aria-describedby');
      }
      this.querySelector('[data-size-calculate]').disabled = !this.config || (four ? !fourMeasurements(this.config.profile, this.answers) : chest && chestEntered(this.answers) ? measurement(this.answers, 'chest') === null : (!weightBands && measurement(this.answers, 'height') === null) || measurement(this.answers, 'weight') === null);
    }
    calculate() {
      this.invalidate();
      if (!this.config || !nativeContext(this, this.config)) { this.status(this.message('options_changed')); return; }
      this.result = recommend(this.config, this.answers);
      if (!this.result) { this.status(this.message(this.config.profile.version === 5 ? 'four_no_match' : 'no_match')); return; }
      this.querySelector('[data-size-answers]').hidden = true; this.querySelector('[data-size-result]').hidden = false;
      const grouped = Boolean(this.result.choices);
      const four = this.config.profile.version === 5;
      this.text('[data-size-heading]', this.message(this.result.multipleSizes ? 'four_multiple_heading' : grouped && !four ? 'weight_waist_heading' : 'result_heading', { size: this.result.label }));
      const height = this.formattedMeasurement(this.answers, 'height');
      const weight = this.formattedMeasurement(this.answers, 'weight');
      const chest = this.result.basis === 'chest' ? this.formattedMeasurement(this.answers, 'chest') : '';
      this.text('[data-size-summary]', this.message(this.result.basis === 'chest' ? 'chest_result_summary' : this.config.profile.version === 3 ? 'weight_result_summary' : 'result_summary', { height, weight, chest, cm: Math.round(this.result.chestCm), fit: this.message(`fit_${this.answers.fit}`) }));
      if (four) {
        const measurements = Object.entries(MEASUREMENTS).filter(([key]) => this.result.usedMeasurements.includes(key))
          .map(([, dimension]) => `${this.message(dimension)}: ${this.formattedMeasurement(this.answers, dimension)}`).join(' · ');
        this.text('[data-size-summary]', this.message('four_result_summary', { measurements }));
      }
      const refine = this.querySelector('[data-size-refine]');
      if (refine) {
        refine.hidden = !this.result.multipleSizes;
        this.text('[data-size-refine]', this.message(this.result.canRefine ? 'four_multiple_refine' : 'four_multiple_guidance'));
      }
      this.text('[data-size-select]', this.message('select_size', { size: this.result.label }));
      const context = nativeContext(this, this.config);
      const select = this.querySelector('[data-size-select]');
      select.hidden = grouped;
      select.disabled = grouped || !context || context.product.hasAttribute('data-pending') || !targetInput(context, this.result);
      const length = this.querySelector('[data-size-length]');
      if (length) length.hidden = !grouped || this.result.choices.length === 0;
      this.text('[data-size-length] legend', this.message(four ? 'four_choices_heading' : 'weight_length_heading'));
      const choices = this.querySelector('[data-size-choices]');
      choices?.replaceChildren();
      if (grouped && choices) for (const choice of this.result.choices) {
        const button = this.ownerDocument.createElement('button');
        button.type = 'button'; button.dataset.sizeChoice = choice.optionValueId; button.textContent = choice.label;
        button.disabled = !context || context.product.hasAttribute('data-pending') || !targetInput(context, { ...this.result, optionValueId: choice.optionValueId });
        choices.append(button);
      }
      const unavailable = grouped ? !choices?.querySelector('button:not(:disabled)') : select.disabled;
      if (unavailable) this.text('[data-size-status]', this.message(this.result.multipleSizes && this.result.choices?.length === 0 ? 'four_sizes_missing' : four && this.result.choices?.length === 0 ? 'four_size_missing' : 'result_unavailable'));
      this.querySelector('[data-size-heading]').focus();
    }
    status(message) { this.text('[data-size-status]', message); this.querySelector('[data-size-status]')?.focus(); }
    selectSize(choiceId) {
      if (!this.result || !this.config || this.attempt) return;
      const choice = this.result.choices?.find(item => item.optionValueId === choiceId);
      if (this.result.choices && !choice) return;
      const context = nativeContext(this, this.config); const requested = { ...this.result, ...(choice && { optionValueId: choice.optionValueId, label: choice.label }) };
      const input = context && targetInput(context, requested);
      if (!context || !input || context.product.hasAttribute('data-pending') || context.picker.inert || context.picker.closest('[inert]')) { this.invalidate(); this.status(this.message('choice_unavailable')); return; }
      const expected = { ...context.selected, [requested.optionPosition]: requested.optionValueId };
      const controller = new scope.AbortController(); const signal = controller.signal;
      const attempt = this.attempt = { controller, timeout: null };
      const fail = message => { if (this.attempt !== attempt) return; this.invalidate(); this.updateAnswers(); this.status(message); };
      context.form.addEventListener('variant:change', event => {
        if (event.target !== context.form || this.attempt !== attempt) return;
        const current = nativeContext(this, this.config); const variant = event.detail?.variant;
        const optionsMatch = current && Object.entries(expected).every(([position, id]) => current.selected[position] === id);
        const namesMatch = Array.isArray(variant?.options) && variant.options.length === this.config.options.length && this.config.options.every(option => variant.options[option.position - 1] === option.values.find(value => value.id === expected[option.position])?.name);
        const idField = current?.form.elements.namedItem('id');
        if (!current || !optionsMatch || !namesMatch || !variant?.id || variant.available !== true || String(current.picker.selectedVariant?.id) !== String(variant.id) || idField?.value !== String(variant.id) || !targetInput(current, requested)) {
          fail(this.message('confirm_error')); return;
        }
        this.cancelAttempt(); this.back(false);
        this.text('[data-size-status]', this.message('selected'));
      }, { signal });
      const error = () => fail(this.message('load_error'));
      context.picker.addEventListener('vl:variant-error', error, { signal });
      context.product.addEventListener('vl:variant-error', error, { signal });
      attempt.timeout = scope.setTimeout(() => fail(this.message('timeout')), 10000);
      this.setAttribute('aria-busy', 'true'); this.querySelector('[data-size-select]').disabled = true;
      for (const button of this.querySelectorAll('[data-size-choice]')) button.disabled = true;
      this.text('[data-size-status]', this.message('checking'));
      this.dispatching = true;
      try { input.checked = true; input.dispatchEvent(new scope.Event('change', { bubbles: true })); }
      catch { error(); }
      finally { this.dispatching = false; }
    }
  }
  scope.customElements.define('raith-size-calculator', RaithSizeCalculator);
}
registerSizeCalculator();
